import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;
  searchNameName: string;
  numberOfContactsToLoad: number;

  handleCreateSearchNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleCreateSearchNumberOfContactsChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleCreateNewSearch(): void;
}

const CreateNewSearchDialog: React.FC<Props> = ({
  open,
  loading,
  searchNameName,
  numberOfContactsToLoad,
  handleCreateSearchNameChange,
  handleCreateSearchNumberOfContactsChange,
  handleClose,
  handleCreateNewSearch,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog classes={{ paper: classes.paper }} open={open} fullWidth={true} maxWidth={'sm'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('nameYourSearch')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <PaiTextField
            value={searchNameName}
            placeholder={t('insertSearchNamePlaceholder')}
            variant={'background'}
            onChange={handleCreateSearchNameChange}
          />
          <Divider />
          <Stack gap={2}>
            <Stack gap={1}>
              <Typography className={classes.boldText}>{t('setTotalContactsTitle')}</Typography>
              <Typography className={classes.greyText}>{t('setTotalContactsDescriptionShort')}</Typography>
            </Stack>
            <Stack gap={1}>
              <PaiTextField
                value={numberOfContactsToLoad}
                variant={'background'}
                type={'number'}
                onChange={handleCreateSearchNumberOfContactsChange}
                style={{ maxWidth: '350px' }}
              />
              {numberOfContactsToLoad > 150 ? (
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <PaiHelpIcon tooltipText={false} variant={'warning'} />
                  <Typography className={classes.warningText}>{t('contactsLoadingLimitWarning', { count: 150 })}</Typography>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('ok')}
          action={handleCreateNewSearch}
          size={'small'}
          border={'rounded'}
          disabled={searchNameName.trim() === '' || !numberOfContactsToLoad}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  warningText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.warning.main,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default CreateNewSearchDialog;
